/**
 * Every visible amount on a money surface, formatted for the reader's language.
 *
 * WHY THIS FILE EXISTS
 * The same eight-line formatter was copy-pasted into three screens — Pricing, Order
 * and Billing — and all three hardcoded the POLISH decimal comma:
 *
 *     whole.toFixed(2).replace('.', ',')
 *
 * unconditionally, with no locale in sight. So the English pricing page quoted
 * "29,99 €" to a reader whose decimal separator is a dot. CRITIC found it while
 * checking something else, and was generous about it ("an ordering gap, not
 * negligence" — the separator registry lands after my slot). It is worth being
 * blunter: I shot the English screenshots, looked at them repeatedly, and read
 * "29,99 €" without registering it. Money surfaces were reviewed in Polish, by a
 * Pole, and that is exactly the blindness that lets a localisation defect live on
 * the one page where a customer decides to pay.
 *
 * Three copies were also three chances to fix two of them and think it done, so the
 * duplication is removed rather than patched three times.
 *
 * WHY NOT `Intl.NumberFormat`
 * Same reason the thousands grouping next door is hand-rolled: the result depends on
 * the ICU data in whatever runtime renders the page. This project's Node build
 * returns a bare "3500" where a full-ICU build returns "3,500" — and a comma reads
 * as a DECIMAL point to a European customer. On a price, an environment-dependent
 * separator is not a rendering detail; it is a different number. Two locales with a
 * known separator each is a decision, not a guess.
 */

export type MoneyLocale = 'en' | 'pl'

/** The decimal mark the reader expects. Polish uses a comma, English a dot. */
function decimalMark(locale: MoneyLocale): string {
  return locale === 'en' ? '.' : ','
}

/**
 * Minor units → a bare display amount: "29,99" in Polish, "29.99" in English.
 *
 * Whole amounts stay whole ("30", never "30,00"): the catalogue prices are the
 * marketing figures, and a trailing ",00" reads as an invoice rather than an offer.
 */
export function amount(cents: number, locale: MoneyLocale): string {
  const whole = cents / 100
  if (Number.isInteger(whole)) return String(whole)
  return whole.toFixed(2).replace('.', decimalMark(locale))
}

/** The symbol a customer recognises; the ISO code is a fallback, never a design. */
export function currencySymbol(currency: string): string {
  return currency === 'EUR' ? '€' : currency === 'PLN' ? 'zł' : currency
}

/**
 * A complete price for display: "29,99 €" / "29.99 €".
 *
 * THE SPACE IS A NON-BREAKING SPACE, AND THAT IS THE WHOLE POINT (D4).
 * With an ordinary space the browser may break the line between the number and the
 * symbol, and it did: on the invoice card at 1440 the amount read "61,57" with "€"
 * alone on the next line. An amount is ONE fact — the figure without its unit is not
 * a smaller version of the price, it is a different thing — so the pairing must not
 * be a place the renderer is allowed to break.
 *
 * Fixed here rather than with `white-space: nowrap` on the one card where it was
 * spotted: every screen that shows money joins these two pieces, so a CSS fix would
 * have to be repeated on each of them and would be missing from the next one. It is
 * also what Polish typography asks for independently of any wrapping bug.
 */
export function price(cents: number, currency: string, locale: MoneyLocale): string {
  return `${amount(cents, locale)}\u00a0${currencySymbol(currency)}`
}

/**
 * A gem count as ONE number: "3 240", never "3240".
 *
 * Sesja 11 (G7-account-token2-22): the balance used to be grouped with an ordinary
 * ASCII space and set in monospace at 52 px, so "3 240" read as two numbers. Hand-rolled
 * for the same reason `amount()` is: the grouping must not depend on the ICU data of the
 * runtime that renders the page.
 *
 * PACZKA P1 B (21.09): WHICH mark goes between the groups is no longer decided here. Two
 * formatters (this one and `locales/format.formatNumber`) had drifted onto two different
 * characters, so two screens next to each other showed two different gaps. The separator
 * now has one definition for the whole product, in `locales/format.ts`, with the browser
 * measurements that chose it; re-exported here so a money surface does not have to know
 * which of the two files the currency's typography lives in.
 *
 * PACZKA N11 (P1-05): the grouping itself moved there too, as `locales/format.groupCount`,
 * because it has to know the reader's language: English groups a token count with a comma,
 * like every other English number on the screen next to it.
 */
export { GROUP_SEP } from '../locales/format'
